import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import {
  ApplicationType,
  DataSourceType,
  ScanSession,
} from "@/views/customize/data-sources/data/model";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { UseLoadingAction } from "@/common/composition/loading";
import { UserSettings } from "@/common/api/user-settings.api";
import { Ref, computed, ref } from "@vue/composition-api";
import {
  get_finding_count,
  get_finding_entities,
} from "@/common/api/findings/findings.api";
import { SharedState } from "@/common/state";
import { AzFileShareItem } from "./model";
import { UseCollection } from "@/common/composition/use-collection";
import { getInfoType } from "@/common/state/infotypes";
import { uniqby } from "@/fp";
import { ICONS } from "@/common/icons.map";

type CountItem = {
  id: string;
  name: string;
  count: number;
  generation: number;
  code: number;
};

const AzFileShareLabelsSettings = UserSettings.key<string[]>(
  "az_file_share",
  () => []
);

const [loading, set_loading] = UseLoadingAction<
  "init" | "change_session" | "change_labels"
>(false, "init");

const selected_labels: Ref<string[]> = ref([]);

const [
  sessions,
  selected_session,
  _set_selected_session,
  init_sessions,
] = UseDropdownItems<ScanSession>([]);

const az_file_share_row_items: Ref<Array<CountItem>> = ref([]);
const selected_az_file_share_row_item: Ref<null | CountItem> = ref(null);

const az_file_share_items = UseCollection<AzFileShareItem, { cursor: string }>(
  // @ts-ignore
  async ({ cursor }) => {
    return await get_finding_entities("FileShare")({
      application: "az_file_share",
      service: "az_file_share",
      axis_z: {
        type: "Session",
        value: selected_session.value!.id,
      },
      type: "sensitive_storage_obj",
      cursor,
      label_ids: [selected_az_file_share_row_item.value!.id],
    });
  }
);

const set_selected_file_share_row_item = async (item: CountItem | null) => {
  if (item) {
    const label = SharedState.getInfoType(item.id);
    if (SharedState.restrictions.is_restricted_info(label.code)) {
      SharedState.restrictions.show_restrictions_modal("info", label.labelName);
      return;
    }
    selected_az_file_share_row_item.value = item;
    az_file_share_items.clear();
    await az_file_share_items.load_items({ cursor: "" });
  } else {
    selected_az_file_share_row_item.value = null;
    az_file_share_items.clear();
  }
};

const load_items = async () => {
  const res = await get_finding_count({
    application: "az_file_share" as ApplicationType,
    service: "az_file_share" as DataSourceType,
    axis_z: {
      type: "Session",
      value: selected_session.value!.id,
    },
    label_ids: selected_labels.value,
    type: "sensitive_storage_obj",
  });
  if (res.success) {
    az_file_share_row_items.value = res.value
      .filter((f) => SharedState.allInfoTypes.value[f.labelId])
      .map((d) => {
        const label = SharedState.allInfoTypes.value[d.labelId];
        return {
          id: d.labelId,
          count: d.count,
          name: label.labelName,
          generation: label.generation,
          code: label.code,
        };
      });

    if (az_file_share_row_items.value.length > 0) {
      let item = null;
      for (const l0 of l0_labels.value) {
        item = az_file_share_row_items.value.find(
          (f) => f.code == l0.code && f.count > 0
        );
        if (item) {
          break;
        }
      }
      if (item) {
        _selected_l0_code.value = item.code;
        set_selected_file_share_row_item(item);
      } else {
        _selected_l0_code.value = l0_labels.value[0].code;
      }
    }
  }
};

const set_selected_session = async (session: ScanSession) => {
  // @ts-ignore
  _set_selected_session(session);
  set_selected_file_share_row_item(null);
  if (selected_labels_count.value > 0) {
    set_loading(true, "change_session");
    await load_items();
    set_loading(false, "change_session");
  }
};

const selected_labels_count = computed(() => selected_labels.value.length);

const _selected_l0_code = ref(-1);
const selected_l0_code = computed(() => _selected_l0_code.value);

const set_selected_l0_code = (code: number) => {
  _selected_l0_code.value = code;
  const item = az_file_share_row_items.value.find(
    (f) => f.code == code && f.count > 0
  );
  az_file_share_items.clear();
  if (item) {
    set_selected_file_share_row_item(item);
  } else {
    const l0 = az_file_share_row_items.value.find(
      (f) => f.code == code && f.generation == 0
    )!;
    if (l0) {
      selected_az_file_share_row_item.value = l0;
    }
  }
};
const l0_labels = computed(() => {
  const labels = selected_labels.value.map((label_id) => {
    const { labelName, code, id } = getInfoType(label_id)!;
    return {
      id,
      code,
      name: labelName,
      l0_name: SharedState.get_l0_by_code(code)!.labelName,
      icon: ICONS.L0_ICONS.get(code),
    };
  });

  return uniqby("code")(labels) as Array<{
    id: string;
    name: string;
    l0_name: string;
    icon: string;
    code: number;
  }>;
});

const save_selected_labels_ids = async (ids: string[]) => {
  selected_labels.value = ids;
  set_loading(true, "change_labels");
  await load_items();
  await AzFileShareLabelsSettings.update(ids);
  set_loading(false, "change_labels");
};

const init = async (force = false) => {
  set_loading(true, "init");
  selected_labels.value = await AzFileShareLabelsSettings.get();
  selected_labels.value = selected_labels.value.filter(
    (id) => SharedState.allInfoTypes.value[id]
  );
  await data_sources_state.init();
  const res = await data_sources_state.ext.get_sessions_for_source(
    "az_file_share"
  );
  if (res.success) {
    init_sessions(res.value);
    if (sessions.value.length > 0) {
      if (force) {
        set_selected_session(sessions.value[0]);
      } else {
        set_selected_session(selected_session.value || sessions.value[0]);
      }
    }
  }
  set_loading(false, "init");
};

export const state = {
  init,
  loading,
  az_file_share_row_items,
  sessions,
  selected_session,
  set_selected_session,
  selected_labels_count,
  save_selected_labels_ids,
  az_file_share_items,
  selected_az_file_share_row_item,
  set_selected_file_share_row_item,
  selected_labels,
  l0_labels,
  set_selected_l0_code,
  selected_l0_code,
};
